import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Receipt, DollarSign, Droplets, CheckCircle2, CreditCard, Zap, ChevronDown, ChevronUp, Download, FileText, Mail, Inbox, ArrowRight, X, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { currentColomboMonthYear, formatDate } from '../lib/datetime';
import { MonthlyStatementCard } from '../components/MonthlyStatementCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const InvoiceRow = ({ invoice, onPayOnline, onMarkPaid, onRecordPayment, onSubmitPayment, onEmailStatement, isAdmin }) => {
  const paid = invoice.status === 'paid';
  const partial = invoice.status === 'partial';
  const paidAmount = Number(invoice.paid_amount ?? 0);
  const balance = Number(invoice.balance ?? (invoice.total_due ?? invoice.amount));
  const totalDue = Number(invoice.total_due ?? invoice.amount);
  const isCommonWater = invoice.invoice_type === 'common_water';
  const isCommonElec = invoice.invoice_type === 'common_electricity';
  const isCommonAdd = invoice.invoice_type === 'common_additional';
  const isWater = invoice.invoice_type === 'water' || isCommonWater;
  const period = `${MONTH_NAMES[invoice.period_month - 1]} ${invoice.period_year}`;
  const invoiceLabel =
    isCommonAdd ? 'Additional Common Work'
    : isCommonElec ? 'Common Electricity Bill'
    : isCommonWater ? 'Common Water Bill'
    : isWater ? 'Water Bill'
    : 'Monthly Maintenance';

  return (
    <Card className="p-5 bg-white border border-[#e2e8f0] hover:shadow-sm transition-shadow" data-testid={`invoice-card-${invoice._id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className={`h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0 ${
            isCommonAdd ? 'bg-violet-100' :
            isCommonElec ? 'bg-amber-100' :
            isCommonWater ? 'bg-teal-100' :
            isWater ? 'bg-cyan-100' : 'bg-blue-100'
          }`}>
            {isCommonAdd ? <Wrench className="h-5 w-5 text-violet-600" />
              : isCommonElec ? <Zap className="h-5 w-5 text-amber-600" />
              : isWater ? <Droplets className={`h-5 w-5 ${isCommonWater ? 'text-teal-600' : 'text-cyan-600'}`} />
              : <DollarSign className="h-5 w-5 text-blue-600" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="text-base font-semibold" style={{ fontFamily: 'Outfit' }}>
                {invoiceLabel}
              </h3>
              <span className="text-xs text-[#64748b]">•</span>
              <span className="text-xs text-[#64748b]">{period}</span>
              {invoice.invoice_number && (
                <span
                  className="ml-1 px-1.5 py-0.5 text-[10px] font-mono font-semibold rounded bg-slate-100 text-slate-700 border border-slate-200"
                  data-testid={`invoice-number-${invoice._id}`}
                  title="Invoice number"
                >
                  {invoice.invoice_number}
                </span>
              )}
            </div>
            {isAdmin && (
              <p className="text-xs text-[#64748b] mb-1">
                {invoice.resident_name} · Unit {invoice.unit_number || '—'} · {invoice.property_name}
              </p>
            )}
            {isCommonAdd && (
              <>
                {invoice.description && (
                  <p className="text-sm font-medium text-violet-800 mb-1 mt-0.5" data-testid={`common-add-desc-${invoice._id}`}>
                    {invoice.description}
                  </p>
                )}
                <p className="text-xs text-[#64748b] mb-1" data-testid={`common-add-detail-${invoice._id}`}>
                  Total bill: LKR {Number(invoice.total_property_bill || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ·
                  Your share {Number(invoice.share_percent || 0).toFixed(4)}%
                  {invoice.notes ? <span className="italic"> · {invoice.notes}</span> : null}
                </p>
              </>
            )}
            {isCommonElec && (
              <p className="text-xs text-[#64748b] mb-1" data-testid={`common-elec-detail-${invoice._id}`}>
                Property CEB bill: LKR {Number(invoice.total_property_bill || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ·
                Your share {Number(invoice.share_percent || 0).toFixed(4)}%
                {invoice.notes ? <span className="italic"> · {invoice.notes}</span> : null}
              </p>
            )}
            {isCommonWater && (
              <p className="text-xs text-[#64748b] mb-1" data-testid={`common-water-detail-${invoice._id}`}>
                Property meter: {invoice.previous_reading} → {invoice.current_reading} · {invoice.usage} units ·
                Total bill LKR {Number(invoice.total_property_bill || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ·
                Your share {Number(invoice.share_percent || 0).toFixed(4)}%
              </p>
            )}
            {isWater && !isCommonWater && (
              <p className="text-xs text-[#64748b] mb-1">
                Reading: {invoice.previous_reading} → {invoice.current_reading} · Usage {invoice.usage} units
                {typeof invoice.vat_amount === 'number' && invoice.vat_amount > 0 && (
                  <span> · Subtotal LKR {(invoice.subtotal ?? (invoice.amount - invoice.vat_amount)).toFixed(2)} + VAT@{invoice.vat_percent}% (LKR {invoice.vat_amount.toFixed(2)})</span>
                )}
              </p>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                paid ? 'bg-green-100 text-green-700'
                : partial ? 'bg-blue-100 text-blue-700'
                : 'bg-yellow-100 text-yellow-700'
              }`} data-testid={`invoice-status-${invoice._id}`}>
                {paid ? 'Paid' : partial ? 'Partially Paid' : 'Unpaid'}
              </span>
              {paid && invoice.paid_via && (
                <span className="px-2 py-0.5 bg-[#f1f5f9] text-xs font-medium rounded-full capitalize">via {invoice.paid_via}</span>
              )}
              {invoice.due_date && !paid && (
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${invoice.is_overdue ? 'bg-red-100 text-red-800' : 'bg-[#f1f5f9] text-[#64748b]'}`}>
                  {invoice.is_overdue ? 'Overdue since ' : 'Due '}
                  {formatDate(invoice.due_date)}
                </span>
              )}
              {invoice.surcharge_amount > 0.005 && (
                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800" data-testid={`invoice-surcharge-${invoice._id}`}>
                  +LKR {Number(invoice.surcharge_amount).toFixed(2)} late fee (3%)
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-2xl font-semibold" style={{ fontFamily: 'Outfit' }}>
            LKR {balance.toFixed(2)}
          </p>
          <p className="text-[10px] text-[#64748b]" data-testid={`invoice-balance-line-${invoice._id}`}>
            {paid
              ? `paid LKR ${paidAmount.toFixed(2)}`
              : partial
                ? `paid LKR ${paidAmount.toFixed(2)} · balance shown of LKR ${totalDue.toFixed(2)} total`
                : `total LKR ${totalDue.toFixed(2)}`}
          </p>
          {invoice.surcharge_amount > 0.005 && (
            <p className="text-[10px] text-[#64748b]">principal LKR {Number(invoice.amount).toFixed(2)}</p>
          )}
          {!paid && !isAdmin && (
            <div className="mt-2 flex flex-col gap-2 items-end">
              <Button
                onClick={() => onPayOnline(invoice)}
                size="sm"
                className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                data-testid={`pay-invoice-${invoice._id}`}
              >
                <CreditCard className="mr-1 h-4 w-4" /> Pay Now
              </Button>
              {onSubmitPayment && (
                <Button
                  onClick={() => onSubmitPayment(invoice)}
                  size="sm"
                  variant="outline"
                  className="border-[#e2e8f0]"
                  data-testid={`submit-payment-${invoice._id}`}
                >
                  Log Bank / Cash Payment
                </Button>
              )}
            </div>
          )}
          {!paid && isAdmin && (
            <div className="mt-2 flex flex-col gap-2 items-end">
              {onRecordPayment && (
                <Button
                  onClick={() => onRecordPayment(invoice)}
                  size="sm"
                  className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                  data-testid={`record-payment-${invoice._id}`}
                >
                  Record Payment
                </Button>
              )}
              <Button
                onClick={() => onMarkPaid(invoice._id)}
                size="sm"
                variant="outline"
                className="text-[#10b981] border-[#10b981] hover:bg-green-50"
                data-testid={`mark-paid-${invoice._id}`}
              >
                <CheckCircle2 className="mr-1 h-4 w-4" /> Mark Fully Paid
              </Button>
              {onEmailStatement && (
                <Button
                  onClick={() => onEmailStatement(invoice)}
                  size="sm"
                  variant="outline"
                  className="text-[#2563eb] border-[#2563eb]/40 hover:bg-[#2563eb]/5"
                  data-testid={`email-statement-${invoice._id}`}
                >
                  <Mail className="mr-1 h-4 w-4" /> Email Statement
                </Button>
              )}
            </div>
          )}
          {paid && isAdmin && onEmailStatement && (
            <div className="mt-2 flex justify-end">
              <Button
                onClick={() => onEmailStatement(invoice)}
                size="sm"
                variant="outline"
                className="text-[#2563eb] border-[#2563eb]/40 hover:bg-[#2563eb]/5"
                data-testid={`email-statement-${invoice._id}`}
              >
                <Mail className="mr-1 h-4 w-4" /> Email Statement
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export const Invoices = ({ view = 'all' }) => {
  // `view` controls which admin sections render. Same component is mounted
  // under 3 routes to give admins a clean, focused page per workflow phase:
  //   - view='list'       → /invoices          (invoice list + filters)
  //   - view='prepare'    → /prepare-bills     (all bill-creation forms)
  //   - view='statements' → /statements        (email monthly statements)
  //   - view='all'        → back-compat (renders everything)
  // Residents ignore the flag and see their own consolidated statement view.
  const showPrepare = view === 'prepare' || view === 'all';
  const showStatements = view === 'statements' || view === 'all';
  const showList = view === 'list' || view === 'all';
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [statements, setStatements] = useState([]);
  const [properties, setProperties] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'management';

  // Billing policy (management-configurable). Read by all roles for accurate
  // "Late fee (X%)" labels; only management can PUT.
  const [billingSettings, setBillingSettings] = useState(null);
  const [billingForm, setBillingForm] = useState({ surcharge_rate_percent: 3, due_days: 30 });
  const [savingBilling, setSavingBilling] = useState(false);

  // Bulk-email statements (management-only). Picks a period and emails every
  // resident with invoices in that period their consolidated statement + PDF.
  const nowCM = currentColomboMonthYear();
  const [emailPeriod, setEmailPeriod] = useState({
    period_month: nowCM.month,
    period_year: nowCM.year,
    property_id: '',   // empty = all properties
  });
  const [emailingStatements, setEmailingStatements] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  // Preview modal state — populated by GET .../email-preview so admin can
  // verify the recipient list + totals BEFORE the actual send.
  const [emailPreview, setEmailPreview] = useState(null);        // response payload
  const [loadingPreview, setLoadingPreview] = useState(false);
  // Per-resident send: tracks resident_id currently being emailed so we can
  // spinner the specific button (rather than disabling the whole modal).
  const [sendingOne, setSendingOne] = useState(null);
  // { residentId, name, period_month, period_year, property_id?, unit_number? } | null
  const [confirmOne, setConfirmOne] = useState(null);

  // Property filter for admin's invoice list. Empty = all properties.
  const [invoicePropertyFilter, setInvoicePropertyFilter] = useState('');
  // Type filter for admin (maintenance / water / common_water / common_electricity).
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState('');
  // Period filter (YYYY-MM) — empty = all periods. Populated from the invoices themselves.
  const [invoicePeriodFilter, setInvoicePeriodFilter] = useState('');
  // Resident filter (resident_id) — empty = all residents.
  const [invoiceResidentFilter, setInvoiceResidentFilter] = useState('');
  // Grouping: how the filtered list is bucketed on screen.
  const [invoiceGroupBy, setInvoiceGroupBy] = useState('property'); // 'property' | 'resident' | 'type' | 'period'

  // ---- Record payment modal (admin) ----
  const [recordPaymentInvoice, setRecordPaymentInvoice] = useState(null);
  const [recordPaymentForm, setRecordPaymentForm] = useState({ amount: '', method: 'cash', note: '' });
  const [savingRecordPayment, setSavingRecordPayment] = useState(false);

  const openRecordPayment = (inv) => {
    setRecordPaymentInvoice(inv);
    setRecordPaymentForm({
      amount: String(Number(inv.balance ?? inv.total_due ?? inv.amount).toFixed(2)),
      method: 'cash',
      note: '',
    });
  };
  const submitRecordPayment = async () => {
    const inv = recordPaymentInvoice;
    if (!inv) return;
    const amt = Number(recordPaymentForm.amount);
    if (!(amt > 0)) { toast.error('Enter an amount greater than 0'); return; }
    setSavingRecordPayment(true);
    try {
      await axios.post(`${API}/admin/payments/record`, {
        invoice_id: inv._id,
        amount: amt,
        method: recordPaymentForm.method,
        note: recordPaymentForm.note || undefined,
      }, { withCredentials: true });
      toast.success(`Recorded LKR ${amt.toFixed(2)}. Balance updated.`);
      setRecordPaymentInvoice(null);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSavingRecordPayment(false);
    }
  };

  // ---- Submit payment modal (resident) ----
  const [submitPaymentInvoice, setSubmitPaymentInvoice] = useState(null);
  const [submitPaymentForm, setSubmitPaymentForm] = useState({ amount: '', method: 'bank_transfer', note: '' });
  const [submitPaymentSlip, setSubmitPaymentSlip] = useState(null);
  const [savingSubmitPayment, setSavingSubmitPayment] = useState(false);

  const openSubmitPayment = (inv) => {
    setSubmitPaymentInvoice(inv);
    setSubmitPaymentForm({
      amount: String(Number(inv.balance ?? inv.total_due ?? inv.amount).toFixed(2)),
      method: 'bank_transfer',
      note: '',
    });
    setSubmitPaymentSlip(null);
  };
  const submitSubmitPayment = async () => {
    const inv = submitPaymentInvoice;
    if (!inv) return;
    const amt = Number(submitPaymentForm.amount);
    if (!(amt > 0)) { toast.error('Enter an amount greater than 0'); return; }
    if (submitPaymentForm.method === 'bank_transfer' && !submitPaymentSlip) {
      toast.error('Please attach the bank transfer slip.');
      return;
    }
    setSavingSubmitPayment(true);
    try {
      // Cascade mode: no invoice_id → backend applies FIFO to oldest unpaid.
      const body = {
        amount: amt,
        method: submitPaymentForm.method,
        note: submitPaymentForm.note || undefined,
      };
      if (!inv.cascade && inv._id) body.invoice_id = inv._id;

      const { data: sub } = await axios.post(`${API}/payments/submit`, body,
        { withCredentials: true });
      if (submitPaymentSlip) {
        const fd = new FormData();
        fd.append('file', submitPaymentSlip);
        await axios.post(`${API}/payments/${sub._id}/upload-slip`, fd, { withCredentials: true });
      }
      toast.success('Payment logged. Awaiting management approval.');
      setSubmitPaymentInvoice(null);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log payment');
    } finally {
      setSavingSubmitPayment(false);
    }
  };

  // ---- Pending approvals (admin) ----
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const loadPendingApprovals = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await axios.get(`${API}/admin/payments/pending`, { withCredentials: true });
      setPendingApprovals(data);
    } catch (err) {
      // Non-critical background load — surface for debugging, don't toast (avoids
      // repeated toasts if the polling loop hits a transient error).
      console.error('Invoices: failed to load pending approvals', err);
    }
  };
  // Refire whenever admin status flips (login as admin/log out). `loadPendingApprovals`
  // is stable across renders (defined at component scope, no closed-over state).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPendingApprovals(); }, [isAdmin]);

  const handleEmailStatements = async () => {
    // Step 1: fetch preview (recipients + totals) so admin can verify BEFORE
    // the actual bulk send. Nothing is sent yet.
    setLoadingPreview(true);
    setEmailResult(null);
    try {
      const params = {
        period_month: emailPeriod.period_month,
        period_year: emailPeriod.period_year,
      };
      if (emailPeriod.property_id) params.property_id = emailPeriod.property_id;
      const { data } = await axios.get(
        `${API}/admin/invoices/monthly-statement/email-preview`,
        { params, withCredentials: true }
      );
      if ((data.total_recipients || 0) === 0) {
        const label = `${MONTH_NAMES_FULL[emailPeriod.period_month - 1]} ${emailPeriod.period_year}`;
        toast.error(`No invoices found for ${label}. Generate invoices first, then try again.`);
        return;
      }
      setEmailPreview(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview recipients');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirmSendStatements = async () => {
    if (!emailPreview) return;
    const label = `${MONTH_NAMES_FULL[emailPeriod.period_month - 1]} ${emailPeriod.period_year}`;
    setEmailingStatements(true);
    try {
      const params = {
        period_month: emailPeriod.period_month,
        period_year: emailPeriod.period_year,
      };
      if (emailPeriod.property_id) params.property_id = emailPeriod.property_id;
      const { data } = await axios.post(
        `${API}/admin/invoices/monthly-statement/email-all`,
        null,
        { params, withCredentials: true }
      );
      setEmailResult(data);
      setEmailPreview(null);
      const scopeLabel = data.property_name ? ` (${data.property_name})` : '';
      if (data.sent_count === data.attempted) {
        toast.success(`Sent ${data.sent_count} statement${data.sent_count === 1 ? '' : 's'} for ${label}${scopeLabel}.`);
      } else if (data.sent_count > 0) {
        toast.warning(`Sent ${data.sent_count}/${data.attempted}${scopeLabel}. ${data.failed_count} failed, ${data.skipped_no_email_count} skipped (no email).`);
      } else {
        toast.error(`No statements were sent for ${label}${scopeLabel}. Check the details below.`);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to email statements';
      toast.error(msg);
    } finally {
      setEmailingStatements(false);
    }
  };

  // Email a single resident's statement (either from the preview modal row
  // or from an admin invoice card). `payload` matches what setConfirmOne
  // stores; the confirm dialog is shown first so admin can double-check.
  const runSendOneStatement = async (payload) => {
    if (!payload) return;
    const { residentId, name, period_month, period_year, property_id, unit_number } = payload;
    setSendingOne(residentId);
    try {
      const params = { resident_id: residentId, period_month, period_year };
      if (property_id) params.property_id = property_id;
      if (unit_number) params.unit_number = unit_number;
      await axios.post(
        `${API}/admin/invoices/monthly-statement/email-one`,
        null,
        { params, withCredentials: true }
      );
      const label = `${MONTH_NAMES_FULL[period_month - 1]} ${period_year}`;
      toast.success(`Statement for ${label} sent to ${name}.`);
      // Refresh preview to reflect anything that might have changed (rare).
      if (emailPreview && emailPreview.period_month === period_month && emailPreview.period_year === period_year) {
        // Optimistically no-op; preview data still valid for the summary.
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to send statement to ${name}`);
    } finally {
      setSendingOne(null);
      setConfirmOne(null);
    }
  };

  useEffect(() => {
    axios.get(`${API}/settings/billing`, { withCredentials: true })
      .then(({ data }) => {
        setBillingSettings(data);
        setBillingForm({
          surcharge_rate_percent: data.surcharge_rate_percent,
          due_days: data.due_days,
        });
      })
      .catch(() => {});
  }, []);

  const handleSaveBilling = async (e) => {
    e.preventDefault();
    if (savingBilling) return;
    const rate = parseFloat(billingForm.surcharge_rate_percent);
    const days = parseInt(billingForm.due_days, 10);
    if (isNaN(rate) || rate < 0 || rate > 100) { toast.error('Surcharge % must be between 0 and 100'); return; }
    if (isNaN(days) || days < 0 || days > 365) { toast.error('Grace period must be between 0 and 365 days'); return; }
    setSavingBilling(true);
    try {
      await axios.put(`${API}/settings/billing`, {
        surcharge_rate_percent: rate,
        due_days: days,
      }, { withCredentials: true });
      const { data } = await axios.get(`${API}/settings/billing`, { withCredentials: true });
      setBillingSettings(data);
      toast.success(`Billing policy saved: ${rate}% after ${days} days`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save billing settings');
    } finally {
      setSavingBilling(false);
    }
  };

  // Admin: monthly generate form (uses current Sri Lanka month/year)
  const nowLK = currentColomboMonthYear();
  const [monthlyForm, setMonthlyForm] = useState({
    property_id: '',
    period_month: nowLK.month,
    period_year: nowLK.year,
  });
  // Per-resident amount overrides: { [resident_id]: amountString }
  const [residentAmounts, setResidentAmounts] = useState({});

  // Admin: water invoice form — property-first, per-resident readings (matches
  // the monthly-maintenance flow). Readings are stored as strings so the input
  // can render both "" and "0" faithfully.
  const [waterForm, setWaterForm] = useState({
    property_id: '',
    period_month: nowLK.month,
    period_year: nowLK.year,
  });
  // Per-unit water readings for multi-unit residents. Keyed by
  // `${resident_id}::${property_id}::${unit_number}` so a resident with 2 units
  // has independent state for each meter.
  const [waterReadings, setWaterReadings] = useState({});
  const [submittingMonthly, setSubmittingMonthly] = useState(false);
  const [submittingWater, setSubmittingWater] = useState(false);

  // Resident-side unit filter (multi-unit residents only). Value is either
  // "all" (default) or a `${property_id}::${unit_number}` key. Filters the
  // list of monthly statement cards to a single unit for easier scanning.
  const [statementUnitFilter, setStatementUnitFilter] = useState('all');
  const [downloadingCombinedPdf, setDownloadingCombinedPdf] = useState(false);

  // Admin: common water bill form — property-level meter reading, split by share%.
  const [commonWaterForm, setCommonWaterForm] = useState({
    property_id: '',
    period_month: nowLK.month,
    period_year: nowLK.year,
    previous_reading: 0,
    current_reading: '',
  });
  const [commonWaterPreview, setCommonWaterPreview] = useState(null);
  const [submittingCommonWater, setSubmittingCommonWater] = useState(false);

  // Admin: common electricity bill form — total LKR from CEB, split by share%.
  const [commonElecForm, setCommonElecForm] = useState({
    property_id: '',
    period_month: nowLK.month,
    period_year: nowLK.year,
    total_bill: '',
    notes: '',
  });
  const [commonElecPreview, setCommonElecPreview] = useState(null);
  const [submittingCommonElec, setSubmittingCommonElec] = useState(false);

  // Admin: additional common work bill form (lift repair, painting, etc.)
  // Same math as common-electricity but description is required and multiple
  // bills per property/month are allowed.
  const [commonAddForm, setCommonAddForm] = useState({
    property_id: '',
    period_month: nowLK.month,
    period_year: nowLK.year,
    total_bill: '',
    description: '',
    notes: '',
  });
  const [commonAddPreview, setCommonAddPreview] = useState(null);
  const [submittingCommonAdd, setSubmittingCommonAdd] = useState(false);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    try {
      if (isAdmin) {
        const [invRes, propRes, userRes] = await Promise.all([
          axios.get(`${API}/admin/invoices`, { withCredentials: true }),
          axios.get(`${API}/properties`, { withCredentials: true }),
          axios.get(`${API}/admin/users`, { withCredentials: true }),
        ]);
        propRes.data.sort((a, b) => {
          const aNum = parseInt((a.name.match(/\d+/) || [0])[0], 10);
          const bNum = parseInt((b.name.match(/\d+/) || [0])[0], 10);
          return aNum - bNum;
        });
        setInvoices(invRes.data);
        setProperties(propRes.data);
        setResidents(userRes.data.filter(u => u.role === 'resident'));
      } else {
        const [invRes, stmtRes] = await Promise.all([
          axios.get(`${API}/invoices/mine`, { withCredentials: true }),
          axios.get(`${API}/invoices/monthly-statements`, { withCredentials: true }),
        ]);
        setInvoices(invRes.data);
        setStatements(stmtRes.data);
      }
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  // Helper: expand every resident on the given property into (resident, unit)
  // rows. Multi-unit residents contribute one row per unit; single-unit
  // residents contribute exactly one row. Key format:
  // `${resident_id}::${property_id}::${unit_number}` — matches the pattern
  // already used by the water flow so both admin billing forms stay symmetric.
  const buildMeterRows = (targetPropertyId) => {
    const rows = [];
    for (const r of residents) {
      const units = Array.isArray(r.units) && r.units.length
        ? r.units.filter(u => u.property_id === targetPropertyId)
        : (r.property_id === targetPropertyId
            ? [{ property_id: r.property_id, unit_number: r.unit_number,
                 monthly_fee: r.monthly_fee, share_percent: r.share_percent }]
            : []);
      for (const u of units) {
        rows.push({
          resident: r,
          unit_number: u.unit_number || '',
          monthly_fee: u.monthly_fee,
          key: `${r._id}::${targetPropertyId}::${u.unit_number || ''}`,
        });
      }
    }
    return rows;
  };

  // When the selected property changes, prefill each (resident, unit) row's
  // amount with that unit's own configured monthly fee (falls back to 0).
  useEffect(() => {
    if (!monthlyForm.property_id) {
      setResidentAmounts({});
      return;
    }
    const meterRows = buildMeterRows(monthlyForm.property_id);
    const next = {};
    meterRows.forEach(mr => {
      const preferred = mr.monthly_fee != null ? String(mr.monthly_fee) : '';
      next[mr.key] = residentAmounts[mr.key] !== undefined ? residentAmounts[mr.key] : preferred;
    });
    setResidentAmounts(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyForm.property_id, residents]);

  const handleGenerateMonthly = async (e) => {
    e.preventDefault();
    if (submittingMonthly) return;
    if (!monthlyForm.property_id) {
      toast.error('Please select a property');
      return;
    }
    const meterRows = buildMeterRows(monthlyForm.property_id);
    if (meterRows.length === 0) {
      toast.error('No residents/units assigned to this property');
      return;
    }
    // One item per (resident, unit) — critical for multi-unit residents so
    // each unit is billed its own configured fee instead of sharing one
    // resident-level amount.
    const items = meterRows.map(mr => ({
      resident_id: mr.resident._id,
      unit_number: mr.unit_number,
      amount: parseFloat(residentAmounts[mr.key]) || 0,
    }));
    if (items.every(i => i.amount <= 0)) {
      toast.error('Enter an amount for at least one unit');
      return;
    }
    setSubmittingMonthly(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/invoices/generate-monthly`,
        { ...monthlyForm, items },
        { withCredentials: true }
      );
      toast.success(`${data.property}: created ${data.created}, skipped ${data.skipped} (of ${data.total_residents})`);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoices');
    } finally {
      setSubmittingMonthly(false);
    }
  };

  // When the water property changes, load previous readings in parallel for
  // every (resident, unit) pair on that property. Multi-unit residents get
  // multiple rows — one per meter — because each unit has its own meter and
  // its own reading history.
  useEffect(() => {
    if (!waterForm.property_id) {
      setWaterReadings({});
      return;
    }
    // Expand each resident into their per-property units. A resident with 2
    // units on this property becomes 2 rows.
    const rows = [];
    for (const r of residents) {
      const units = Array.isArray(r.units) && r.units.length
        ? r.units.filter(u => u.property_id === waterForm.property_id)
        : (r.property_id === waterForm.property_id
            ? [{ property_id: r.property_id, unit_number: r.unit_number }]
            : []);
      for (const u of units) {
        rows.push({ resident: r, unit_number: u.unit_number || '' });
      }
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(rows.map(async ({ resident, unit_number }) => {
        const key = `${resident._id}::${waterForm.property_id}::${unit_number}`;
        try {
          const { data } = await axios.get(
            `${API}/admin/invoices/last-water-reading/${resident._id}`,
            {
              params: { property_id: waterForm.property_id, unit_number },
              withCredentials: true,
            }
          );
          return [key, { previous: Number(data.previous_reading) || 0, current: '' }];
        } catch (_) {
          return [key, { previous: 0, current: '' }];
        }
      }));
      if (cancelled) return;
      setWaterReadings(prev => {
        const next = {};
        for (const [k, val] of entries) {
          const kept = prev[k]?.current;
          next[k] = { previous: val.previous, current: kept !== undefined ? kept : val.current };
        }
        return next;
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waterForm.property_id, residents]);

  const handleWaterSubmit = async (e) => {
    e.preventDefault();
    if (submittingWater) return;
    if (!waterForm.property_id) {
      toast.error('Please select a property');
      return;
    }
    // Iterate every (resident, unit) row on this property. Multi-unit
    // residents contribute multiple rows — one per meter.
    const rows = [];
    for (const r of residents) {
      const units = Array.isArray(r.units) && r.units.length
        ? r.units.filter(u => u.property_id === waterForm.property_id)
        : (r.property_id === waterForm.property_id
            ? [{ property_id: r.property_id, unit_number: r.unit_number }]
            : []);
      for (const u of units) {
        const key = `${r._id}::${waterForm.property_id}::${u.unit_number || ''}`;
        rows.push({ r, unit_number: u.unit_number || '', key, reading: waterReadings[key] });
      }
    }
    const toSubmit = rows.filter(({ reading }) => {
      if (!reading) return false;
      const v = String(reading.current).trim();
      return v !== '' && !isNaN(parseFloat(v));
    });
    if (toSubmit.length === 0) {
      toast.error('Enter a current reading for at least one meter');
      return;
    }
    setSubmittingWater(true);
    let created = 0;
    let skipped = 0;
    const errors = [];
    for (const { r, unit_number, reading } of toSubmit) {
      try {
        await axios.post(`${API}/admin/invoices/water`, {
          resident_id: r._id,
          current_reading: parseFloat(reading.current),
          period_month: waterForm.period_month,
          period_year: waterForm.period_year,
          unit_number,
          property_id: waterForm.property_id,
        }, { withCredentials: true });
        created += 1;
      } catch (error) {
        const detail = error.response?.data?.detail || 'Failed';
        if (error.response?.status === 400 || error.response?.status === 409) {
          skipped += 1;
        } else {
          errors.push(`${r.name} · ${unit_number || '—'}: ${detail}`);
        }
      }
    }
    setSubmittingWater(false);
    if (errors.length > 0) {
      toast.error(`Created ${created}, skipped ${skipped}, ${errors.length} error(s). First: ${errors[0]}`);
    } else {
      toast.success(`Water invoices — created ${created}, skipped ${skipped} (already invoiced)`);
    }
    // Reset the current-reading inputs but keep the property + period so admin
    // can review the newly-created invoices immediately.
    setWaterReadings(prev => {
      const next = {};
      for (const rid of Object.keys(prev)) next[rid] = { ...prev[rid], current: '' };
      return next;
    });
    loadAll();
  };

  const handleMarkPaid = async (invoiceId) => {
    if (!window.confirm('Mark this invoice as paid manually?')) return;
    try {
      await axios.patch(`${API}/admin/invoices/${invoiceId}/mark-paid`, {}, { withCredentials: true });
      toast.success('Invoice marked as paid');
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const handlePayOnline = async (invoice) => {
    try {
      const { data } = await axios.post(
        `${API}/invoices/${invoice._id}/pay`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start payment');
    }
  };

  // "Pay all together" — resident settles every unpaid/partial invoice in a
  // single Stripe checkout instead of one-by-one.
  const [payingAll, setPayingAll] = useState(false);
  const handlePayAllOnline = async () => {
    if (payingAll) return;
    setPayingAll(true);
    try {
      const { data } = await axios.post(
        `${API}/invoices/pay-all`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start bulk payment');
      setPayingAll(false);
    }
  };

  const handleDownloadStatementPdf = async (statement) => {
    try {
      // Pass property_id + unit_number so the PDF is scoped to this specific
      // unit for multi-unit residents (harmless for single-unit residents).
      const params = { period_month: statement.period_month, period_year: statement.period_year };
      if (statement.property_id) params.property_id = statement.property_id;
      if (statement.unit_number) params.unit_number = statement.unit_number;
      const resp = await axios.get(
        `${API}/invoices/monthly-statement/pdf`,
        { params, withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const unitTag = statement.unit_number ? `-${String(statement.unit_number).replace(/[^\w-]/g, '')}` : '';
      a.download = `MySPAN-Statement-${statement.period_year}-${String(statement.period_month).padStart(2, '0')}${unitTag}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download statement PDF');
    }
  };

  // Multi-unit residents: single "combined" PDF for a given month, one section
  // per unit + a grand-total footer. Convenient for sending to an accountant.
  const handleDownloadCombinedPdf = async (period_year, period_month) => {
    if (downloadingCombinedPdf) return;
    setDownloadingCombinedPdf(true);
    try {
      const resp = await axios.get(
        `${API}/invoices/monthly-statement/pdf/combined`,
        {
          params: { period_month, period_year },
          withCredentials: true,
          responseType: 'blob',
        }
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `MySPAN-Statement-${period_year}-${String(period_month).padStart(2, '0')}-all-units.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download combined PDF');
    } finally {
      setDownloadingCombinedPdf(false);
    }
  };

  // Water tariff (loaded once on mount) — used to preview the slab-based charge
  const [waterTariff, setWaterTariff] = useState(null);
  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/water-tariff`, { withCredentials: true })
      .then(({ data }) => setWaterTariff(data))
      .catch(() => {});
  }, [isAdmin]);

  const selectedWaterProperty = properties.find(p => p._id === waterForm.property_id);
  const selectedCommonProperty = properties.find(p => p._id === commonWaterForm.property_id);

  // When common-water property changes, auto-fetch the previous property-level reading.
  useEffect(() => {
    if (!commonWaterForm.property_id) {
      setCommonWaterPreview(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/admin/invoices/common-water/last-reading/${commonWaterForm.property_id}`, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        setCommonWaterForm(prev => ({ ...prev, previous_reading: Number(data.previous_reading) || 0 }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [commonWaterForm.property_id]);

  // Live preview: ask the backend for the total + per-resident split whenever the
  // current-reading (or period) changes. Debounced so we don't hammer on keystrokes.
  useEffect(() => {
    if (!commonWaterForm.property_id) { setCommonWaterPreview(null); return; }
    const cur = parseFloat(commonWaterForm.current_reading);
    if (isNaN(cur) || cur < 0) { setCommonWaterPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/admin/invoices/common-water/preview`, {
          property_id: commonWaterForm.property_id,
          current_reading: cur,
          previous_reading: Number(commonWaterForm.previous_reading) || 0,
          period_month: commonWaterForm.period_month,
          period_year: commonWaterForm.period_year,
        }, { withCredentials: true });
        setCommonWaterPreview(data);
      } catch (_) {
        setCommonWaterPreview(null);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [commonWaterForm.property_id, commonWaterForm.current_reading, commonWaterForm.previous_reading, commonWaterForm.period_month, commonWaterForm.period_year]);

  const handleCommonWaterSubmit = async (e) => {
    e.preventDefault();
    if (submittingCommonWater) return;
    if (!commonWaterForm.property_id) { toast.error('Please select a property'); return; }
    const cur = parseFloat(commonWaterForm.current_reading);
    if (isNaN(cur) || cur < 0) { toast.error('Enter a valid current reading'); return; }
    if (cur < Number(commonWaterForm.previous_reading || 0)) {
      toast.error('Current reading is less than previous reading');
      return;
    }
    setSubmittingCommonWater(true);
    try {
      const { data } = await axios.post(`${API}/admin/invoices/common-water`, {
        property_id: commonWaterForm.property_id,
        current_reading: cur,
        previous_reading: Number(commonWaterForm.previous_reading) || 0,
        period_month: commonWaterForm.period_month,
        period_year: commonWaterForm.period_year,
      }, { withCredentials: true });
      toast.success(`Common water bill: LKR ${Number(data.total_bill).toLocaleString()} · created ${data.created} · skipped ${data.skipped + data.zero_share_skipped}`);
      setCommonWaterForm(prev => ({ ...prev, current_reading: '', previous_reading: cur }));
      setCommonWaterPreview(null);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create common water bill');
    } finally {
      setSubmittingCommonWater(false);
    }
  };

  const selectedCommonElecProperty = properties.find(p => p._id === commonElecForm.property_id);

  // Live 300ms debounced preview for the common-electricity split.
  useEffect(() => {
    if (!commonElecForm.property_id) { setCommonElecPreview(null); return; }
    const total = parseFloat(commonElecForm.total_bill);
    if (isNaN(total) || total <= 0) { setCommonElecPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/admin/invoices/common-electricity/preview`, {
          property_id: commonElecForm.property_id,
          total_bill: total,
          period_month: commonElecForm.period_month,
          period_year: commonElecForm.period_year,
        }, { withCredentials: true });
        setCommonElecPreview(data);
      } catch (_) {
        setCommonElecPreview(null);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [commonElecForm.property_id, commonElecForm.total_bill, commonElecForm.period_month, commonElecForm.period_year]);

  const handleCommonElecSubmit = async (e) => {
    e.preventDefault();
    if (submittingCommonElec) return;
    if (!commonElecForm.property_id) { toast.error('Please select a property'); return; }
    const total = parseFloat(commonElecForm.total_bill);
    if (isNaN(total) || total <= 0) { toast.error('Enter a valid total bill amount'); return; }
    setSubmittingCommonElec(true);
    try {
      const { data } = await axios.post(`${API}/admin/invoices/common-electricity`, {
        property_id: commonElecForm.property_id,
        total_bill: total,
        period_month: commonElecForm.period_month,
        period_year: commonElecForm.period_year,
        notes: commonElecForm.notes.trim() || null,
      }, { withCredentials: true });
      toast.success(`Common electricity: LKR ${Number(data.total_bill).toLocaleString()} · created ${data.created} · skipped ${data.skipped + data.zero_share_skipped}`);
      setCommonElecForm(prev => ({ ...prev, total_bill: '', notes: '' }));
      setCommonElecPreview(null);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create common electricity bill');
    } finally {
      setSubmittingCommonElec(false);
    }
  };

  const selectedCommonAddProperty = properties.find((p) => p._id === commonAddForm.property_id);

  // Live 300ms debounced preview for the additional-common-work split.
  useEffect(() => {
    if (!commonAddForm.property_id) { setCommonAddPreview(null); return; }
    const total = parseFloat(commonAddForm.total_bill);
    if (isNaN(total) || total <= 0) { setCommonAddPreview(null); return; }
    const description = (commonAddForm.description || '').trim();
    if (!description) { setCommonAddPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/admin/invoices/common-additional/preview`, {
          property_id: commonAddForm.property_id,
          total_bill: total,
          period_month: commonAddForm.period_month,
          period_year: commonAddForm.period_year,
          description,
        }, { withCredentials: true });
        setCommonAddPreview(data);
      } catch (_) {
        setCommonAddPreview(null);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [commonAddForm.property_id, commonAddForm.total_bill, commonAddForm.description, commonAddForm.period_month, commonAddForm.period_year]);

  const handleCommonAddSubmit = async (e) => {
    e.preventDefault();
    if (submittingCommonAdd) return;
    if (!commonAddForm.property_id) { toast.error('Please select a property'); return; }
    const total = parseFloat(commonAddForm.total_bill);
    if (isNaN(total) || total <= 0) { toast.error('Enter a valid total bill amount'); return; }
    const description = (commonAddForm.description || '').trim();
    if (!description) { toast.error('Description is required (e.g., "Lift repair")'); return; }
    setSubmittingCommonAdd(true);
    try {
      const { data } = await axios.post(`${API}/admin/invoices/common-additional`, {
        property_id: commonAddForm.property_id,
        total_bill: total,
        period_month: commonAddForm.period_month,
        period_year: commonAddForm.period_year,
        description,
        notes: commonAddForm.notes.trim() || null,
      }, { withCredentials: true });
      toast.success(`${description}: LKR ${Number(data.total_bill).toLocaleString()} · created ${data.created} invoice${data.created === 1 ? '' : 's'}`);
      setCommonAddForm((prev) => ({ ...prev, total_bill: '', description: '', notes: '' }));
      setCommonAddPreview(null);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create additional common work bill');
    } finally {
      setSubmittingCommonAdd(false);
    }
  };

  // Slab preview helper — computes the same numbers the backend uses so admins
  // see the total each resident will be billed before submitting.
  const computeSlabPreview = (usage) => {
    if (!waterTariff || !waterTariff.slabs) {
      return { usageCharge: 0, service: 0, subtotal: 0, vatPercent: 0, vatAmount: 0, total: 0, rows: [] };
    }
    const ordered = [...waterTariff.slabs].sort(
      (a, b) => (a.max_units == null ? 1e12 : Number(a.max_units)) - (b.max_units == null ? 1e12 : Number(b.max_units))
    );
    let usageCharge = 0;
    let topSlab = ordered[0];
    let prevUpper = 0;
    const rows = [];
    for (const slab of ordered) {
      const upper = slab.max_units == null ? Infinity : Number(slab.max_units);
      if (usage <= prevUpper) break;
      const unitsHere = Math.min(usage, upper) - prevUpper;
      if (unitsHere > 0) {
        const charge = unitsHere * Number(slab.rate_per_unit || 0);
        usageCharge += charge;
        rows.push({ ...slab, units: unitsHere, charge });
        topSlab = slab;
      }
      prevUpper = upper;
    }
    const service = topSlab ? Number(topSlab.monthly_service_charge || 0) : 0;
    const subtotal = usageCharge + service;
    const vatPercent = Math.max(0, Number(waterTariff.vat_percent || 0));
    const vatAmount = Number(((subtotal * vatPercent) / 100).toFixed(2));
    const total = Number((subtotal + vatAmount).toFixed(2));
    return { usageCharge, service, subtotal, vatPercent, vatAmount, total, rows };
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div>
        <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>
          {!isAdmin
            ? 'Invoices'
            : view === 'prepare' ? 'Prepare Bills'
            : view === 'statements' ? 'Statements & Emails'
            : 'Invoices'}
        </h1>
        <p className="text-sm text-[#64748b]">
          {!isAdmin ? 'View and pay your invoices'
            : view === 'prepare' ? 'Generate monthly maintenance, water and common bills. All bill-creation forms live here.'
            : view === 'statements' ? 'Preview and send consolidated monthly statement emails to residents.'
            : 'Browse, filter and manage every invoice created across all properties.'}
        </p>
      </div>

      {isAdmin && showPrepare && (
        <Card className="p-6 bg-white border border-[#e2e8f0]" data-testid="billing-settings-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Billing Policy</h3>
              <p className="text-xs text-[#64748b]">Late-payment surcharge rate and grace period. Changes apply to invoices created from now on — existing invoices keep the rate/grace they were issued with.</p>
            </div>
          </div>
          <form onSubmit={handleSaveBilling} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end" data-testid="billing-settings-form">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Late-Payment Surcharge (%)</Label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={billingForm.surcharge_rate_percent}
                  onChange={(e) => setBillingForm({ ...billingForm, surcharge_rate_percent: e.target.value })}
                  className="pr-8"
                  required
                  data-testid="billing-surcharge-rate-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#64748b] font-medium">%</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Grace Period (Days)</Label>
              <Input
                type="number"
                min="0"
                max="365"
                value={billingForm.due_days}
                onChange={(e) => setBillingForm({ ...billingForm, due_days: e.target.value })}
                required
                data-testid="billing-due-days-input"
              />
            </div>
            <Button
              type="submit"
              disabled={savingBilling}
              className="w-full bg-slate-800 hover:bg-slate-900 text-white disabled:opacity-60"
              data-testid="billing-save-button"
            >
              {savingBilling ? 'Saving…' : 'Save Policy'}
            </Button>
          </form>
          <div className="mt-3 text-xs text-[#64748b]" data-testid="billing-current-summary">
            {billingSettings ? (
              <>
                <span className="font-medium text-[#0f172a]">Currently in effect:</span>{' '}
                {billingSettings.surcharge_rate_percent}% late fee after {billingSettings.due_days} days
                {billingSettings.updated_by && (
                  <span className="text-[#94a3b8]"> · last updated by {billingSettings.updated_by}
                    {billingSettings.updated_at ? ` on ${formatDate(billingSettings.updated_at)}` : ''}
                  </span>
                )}
              </>
            ) : (
              <>Loading current policy…</>
            )}
          </div>
        </Card>
      )}

      {isAdmin && showPrepare && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Monthly generation card */}
          <Card className="p-6 bg-white border border-[#e2e8f0]">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-md bg-blue-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Generate Monthly Invoices</h3>
                <p className="text-xs text-[#64748b]">Select property, then set amount per resident (defaults from property fee)</p>
              </div>
            </div>
            <form onSubmit={handleGenerateMonthly} className="space-y-3" data-testid="monthly-form">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Property</Label>
                <select
                  value={monthlyForm.property_id}
                  onChange={(e) => setMonthlyForm({ ...monthlyForm, property_id: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                  required
                  data-testid="monthly-property-select"
                >
                  <option value="">Select property...</option>
                  {properties.map(p => (
                    <option key={p._id} value={p._id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Month</Label>
                  <select
                    value={monthlyForm.period_month}
                    onChange={(e) => setMonthlyForm({ ...monthlyForm, period_month: parseInt(e.target.value, 10) })}
                    className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                    data-testid="monthly-month-select"
                  >
                    {MONTH_NAMES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Year</Label>
                  <Input type="number" value={monthlyForm.period_year} min={2020} max={2100}
                    onChange={(e) => setMonthlyForm({ ...monthlyForm, period_year: parseInt(e.target.value, 10) })}
                    data-testid="monthly-year-input" />
                </div>
              </div>

              {/* Per-(resident,unit) amount editor. Multi-unit residents get
                  one row per unit, each with its own configured monthly fee
                  so bills reflect per-unit charges accurately. */}
              {monthlyForm.property_id && (() => {
                const meterRows = buildMeterRows(monthlyForm.property_id);
                if (meterRows.length === 0) {
                  return (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-xs text-yellow-800" data-testid="no-residents-warning">
                      No residents/units assigned to this property yet.
                    </div>
                  );
                }
                const totalPreview = meterRows.reduce((s, mr) => s + (parseFloat(residentAmounts[mr.key]) || 0), 0);
                const applyAll = (val) => {
                  const next = { ...residentAmounts };
                  meterRows.forEach(mr => { next[mr.key] = val; });
                  setResidentAmounts(next);
                };
                return (
                  <div className="space-y-2" data-testid="resident-amounts-editor">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">
                        Amount per Unit (LKR)
                      </Label>
                      <button
                        type="button"
                        onClick={() => {
                          const val = window.prompt('Apply this amount (LKR) to every unit:', '');
                          if (val === null) return;
                          const num = parseFloat(val);
                          if (isNaN(num) || num < 0) { toast.error('Enter a valid amount'); return; }
                          applyAll(String(num));
                        }}
                        className="text-xs text-[#2563eb] hover:underline"
                        data-testid="apply-all-amount-button"
                      >
                        Apply same to all
                      </button>
                    </div>
                    <div className="max-h-64 overflow-y-auto border border-[#e2e8f0] rounded-md">
                      <table className="w-full text-sm">
                        <thead className="bg-[#f8fafc] sticky top-0">
                          <tr>
                            <th className="text-left p-2 text-xs font-semibold uppercase text-[#64748b]">Resident</th>
                            <th className="text-left p-2 text-xs font-semibold uppercase text-[#64748b]">Unit</th>
                            <th className="text-right p-2 text-xs font-semibold uppercase text-[#64748b]">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {meterRows.map(mr => (
                            <tr key={mr.key} className="border-t border-[#e2e8f0]" data-testid={`resident-row-${mr.key}`}>
                              <td className="p-2">{mr.resident.name}</td>
                              <td className="p-2 text-[#64748b]">{mr.unit_number || '—'}</td>
                              <td className="p-2 text-right">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={residentAmounts[mr.key] ?? ''}
                                  onChange={(e) => setResidentAmounts({ ...residentAmounts, [mr.key]: e.target.value })}
                                  className="h-8 w-28 ml-auto text-right"
                                  data-testid={`resident-amount-input-${mr.key}`}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex justify-between text-xs text-[#64748b] px-1" data-testid="monthly-total-preview">
                      <span>{meterRows.length} unit{meterRows.length !== 1 ? 's' : ''}</span>
                      <span className="font-semibold text-[#0f172a]">
                        Total: LKR {totalPreview.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                );
              })()}

              <Button type="submit" disabled={submittingMonthly} className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white disabled:opacity-60 disabled:cursor-not-allowed" data-testid="generate-monthly-button">
                {submittingMonthly ? 'Generating…' : 'Generate Invoices'}
              </Button>
            </form>
          </Card>

          {/* Water invoice card — property-first, per-resident readings */}
          <Card className="p-6 bg-white border border-[#e2e8f0]">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-md bg-cyan-100 flex items-center justify-center">
                <Droplets className="h-5 w-5 text-cyan-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Add Water Invoice</h3>
                <p className="text-xs text-[#64748b]">Select a property, then enter each resident's current meter reading</p>
              </div>
            </div>
            <form onSubmit={handleWaterSubmit} className="space-y-3" data-testid="water-form">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Property</Label>
                <select
                  value={waterForm.property_id}
                  onChange={(e) => setWaterForm({ ...waterForm, property_id: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                  required
                  data-testid="water-property-select"
                >
                  <option value="">Select property...</option>
                  {properties.map(p => (
                    <option key={p._id} value={p._id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Month</Label>
                  <select
                    value={waterForm.period_month}
                    onChange={(e) => setWaterForm({ ...waterForm, period_month: parseInt(e.target.value, 10) })}
                    className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                    data-testid="water-month-select"
                  >
                    {MONTH_NAMES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Year</Label>
                  <Input type="number" value={waterForm.period_year} min={2020} max={2100}
                    onChange={(e) => setWaterForm({ ...waterForm, period_year: parseInt(e.target.value, 10) })}
                    data-testid="water-year-input" />
                </div>
              </div>

              {/* Per-resident readings table */}
              {waterForm.property_id && (() => {
                // Expand residents into (resident, unit) rows. A multi-unit
                // resident with 2 units on this property becomes 2 rows.
                const meterRows = [];
                for (const r of residents) {
                  const units = Array.isArray(r.units) && r.units.length
                    ? r.units.filter(u => u.property_id === waterForm.property_id)
                    : (r.property_id === waterForm.property_id
                        ? [{ property_id: r.property_id, unit_number: r.unit_number, water_meter_number: r.water_meter_number }]
                        : []);
                  for (const u of units) {
                    meterRows.push({
                      resident: r,
                      unit_number: u.unit_number || '',
                      meter_no: u.water_meter_number || '',
                      key: `${r._id}::${waterForm.property_id}::${u.unit_number || ''}`,
                    });
                  }
                }
                if (meterRows.length === 0) {
                  return (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-xs text-yellow-800" data-testid="water-no-residents-warning">
                      No residents/units assigned to this property yet.
                    </div>
                  );
                }
                // Live totals across rows that have a valid current reading.
                let totalBill = 0;
                let filledCount = 0;
                for (const mr of meterRows) {
                  const row = waterReadings[mr.key];
                  if (!row) continue;
                  const cur = parseFloat(row.current);
                  if (isNaN(cur)) continue;
                  const usage = Math.max(0, cur - (row.previous || 0));
                  totalBill += computeSlabPreview(usage).total;
                  filledCount += 1;
                }
                return (
                  <div className="space-y-2" data-testid="water-readings-editor">
                    <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">
                      Meter Readings ({meterRows.length} meter{meterRows.length !== 1 ? 's' : ''})
                    </Label>
                    <div className="max-h-72 overflow-y-auto border border-[#e2e8f0] rounded-md">
                      <table className="w-full text-sm">
                        <thead className="bg-[#f8fafc] sticky top-0">
                          <tr>
                            <th className="text-left p-2 text-xs font-semibold uppercase text-[#64748b]">Resident · Unit</th>
                            <th className="text-right p-2 text-xs font-semibold uppercase text-[#64748b]">Previous</th>
                            <th className="text-right p-2 text-xs font-semibold uppercase text-[#64748b]">Current</th>
                            <th className="text-right p-2 text-xs font-semibold uppercase text-[#64748b]">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {meterRows.map(mr => {
                            const row = waterReadings[mr.key] || { previous: 0, current: '' };
                            const cur = parseFloat(row.current);
                            const hasReading = !isNaN(cur);
                            const usage = hasReading ? Math.max(0, cur - (row.previous || 0)) : 0;
                            const preview = hasReading ? computeSlabPreview(usage) : null;
                            return (
                              <tr key={mr.key} className="border-t border-[#e2e8f0]" data-testid={`water-row-${mr.key}`}>
                                <td className="p-2">
                                  <div className="font-medium">{mr.resident.name}</div>
                                  <div className="text-xs text-[#64748b]">
                                    Unit {mr.unit_number || '—'}
                                    {mr.meter_no ? ` · Meter ${mr.meter_no}` : ''}
                                  </div>
                                </td>
                                <td className="p-2 text-right text-[#64748b]">{(row.previous || 0).toFixed(2)}</td>
                                <td className="p-2 text-right">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={row.current}
                                    onChange={(e) => setWaterReadings({
                                      ...waterReadings,
                                      [mr.key]: { ...row, current: e.target.value },
                                    })}
                                    className="h-8 w-24 ml-auto text-right"
                                    placeholder="—"
                                    data-testid={`water-current-input-${mr.key}`}
                                  />
                                </td>
                                <td className="p-2 text-right whitespace-nowrap">
                                  {preview ? (
                                    <span className="text-cyan-700 font-medium" data-testid={`water-preview-total-${mr.key}`}>
                                      LKR {preview.total.toFixed(2)}
                                    </span>
                                  ) : (
                                    <span className="text-[#cbd5e1]">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex justify-between text-xs text-[#64748b] px-1" data-testid="water-total-preview">
                      <span>{filledCount} of {meterRows.length} with reading</span>
                      <span className="font-semibold text-[#0f172a]">
                        Total: LKR {totalBill.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                );
              })()}

              <Button
                type="submit"
                disabled={submittingWater || !waterForm.property_id}
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="submit-water-button"
              >
                {submittingWater ? 'Creating…' : 'Create Water Invoices'}
              </Button>
              {selectedWaterProperty && (
                <p className="text-[10px] text-[#94a3b8] text-center">
                  Residents without a current reading are skipped. Duplicate invoices for the same month are auto-skipped.
                </p>
              )}
            </form>
          </Card>
        </div>
      )}

      {isAdmin && showPrepare && (
        <Card className="p-6 bg-white border border-[#e2e8f0]" data-testid="common-water-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-md bg-teal-100 flex items-center justify-center">
              <Droplets className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Common Water Bill</h3>
              <p className="text-xs text-[#64748b]">Enter the building's common-meter reading; total is split across residents by their ownership share (%)</p>
            </div>
          </div>
          <form onSubmit={handleCommonWaterSubmit} className="space-y-3" data-testid="common-water-form">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Property</Label>
                <select
                  value={commonWaterForm.property_id}
                  onChange={(e) => setCommonWaterForm({ ...commonWaterForm, property_id: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                  required
                  data-testid="common-water-property-select"
                >
                  <option value="">Select property...</option>
                  {properties.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Previous Reading</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={commonWaterForm.previous_reading}
                  onChange={(e) => setCommonWaterForm({ ...commonWaterForm, previous_reading: parseFloat(e.target.value) || 0 })}
                  data-testid="common-water-previous-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Current Reading <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={commonWaterForm.current_reading}
                  onChange={(e) => setCommonWaterForm({ ...commonWaterForm, current_reading: e.target.value })}
                  placeholder="e.g., 150.00"
                  required
                  data-testid="common-water-current-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Month</Label>
                  <select
                    value={commonWaterForm.period_month}
                    onChange={(e) => setCommonWaterForm({ ...commonWaterForm, period_month: parseInt(e.target.value, 10) })}
                    className="w-full h-10 px-2 rounded-md border border-[#e2e8f0] text-sm bg-white"
                    data-testid="common-water-month-select"
                  >
                    {MONTH_NAMES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Year</Label>
                  <Input type="number" value={commonWaterForm.period_year} min={2020} max={2100}
                    onChange={(e) => setCommonWaterForm({ ...commonWaterForm, period_year: parseInt(e.target.value, 10) })}
                    data-testid="common-water-year-input" />
                </div>
              </div>
            </div>

            {commonWaterPreview && (
              <div className="border border-teal-100 rounded-md bg-teal-50/50 p-4 space-y-3" data-testid="common-water-preview">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-teal-700">Usage</div>
                    <div className="font-semibold">{Number(commonWaterPreview.usage).toFixed(2)} units</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-teal-700">Subtotal</div>
                    <div className="font-semibold">LKR {Number(commonWaterPreview.subtotal).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-teal-700">VAT @ {commonWaterPreview.vat_percent}%</div>
                    <div className="font-semibold">LKR {Number(commonWaterPreview.vat_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-teal-700">Total Bill</div>
                    <div className="font-semibold text-teal-800" data-testid="common-water-total-bill">LKR {Number(commonWaterPreview.total_bill).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span className="text-[#334155]">
                    Total assigned share: <b>{Number(commonWaterPreview.total_share_percent).toFixed(4)}%</b> · Allocated: <b>LKR {Number(commonWaterPreview.allocated).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b>
                    {commonWaterPreview.unallocated > 0.01 && (
                      <span className="text-red-600"> · Unallocated: LKR {Number(commonWaterPreview.unallocated).toLocaleString(undefined, { minimumFractionDigits: 2 })} (shares don't sum to 100%)</span>
                    )}
                  </span>
                </div>
                <div className="max-h-56 overflow-y-auto border border-teal-100 rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bg-white sticky top-0">
                      <tr>
                        <th className="text-left p-2 text-[10px] font-semibold uppercase text-teal-700">Resident</th>
                        <th className="text-right p-2 text-[10px] font-semibold uppercase text-teal-700">Share %</th>
                        <th className="text-right p-2 text-[10px] font-semibold uppercase text-teal-700">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commonWaterPreview.splits.map(s => (
                        <tr key={s.resident_id} className="border-t border-teal-100" data-testid={`common-water-split-${s.resident_id}`}>
                          <td className="p-2">
                            <div className="font-medium">{s.resident_name}</div>
                            <div className="text-[10px] text-[#64748b]">Unit {s.unit_number || '—'}</div>
                          </td>
                          <td className="p-2 text-right">{Number(s.share_percent).toFixed(4)}%</td>
                          <td className={`p-2 text-right font-medium ${s.amount > 0 ? 'text-teal-700' : 'text-[#94a3b8]'}`}>
                            LKR {Number(s.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={submittingCommonWater || !commonWaterForm.property_id || !commonWaterForm.current_reading}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="submit-common-water-button"
            >
              {submittingCommonWater ? 'Creating…' : 'Create Common Water Bills'}
            </Button>
            {selectedCommonProperty && (
              <p className="text-[10px] text-[#94a3b8] text-center">
                Residents with 0% share are skipped. If shares don't sum to 100%, the unallocated portion is not billed to anyone.
              </p>
            )}
          </form>
        </Card>
      )}

      {isAdmin && showPrepare && (
        <Card className="p-6 bg-white border border-[#e2e8f0]" data-testid="common-elec-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-md bg-amber-100 flex items-center justify-center">
              <Zap className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Common Electricity Bill</h3>
              <p className="text-xs text-[#64748b]">Enter the total from the CEB monthly bill; it's split across residents by their ownership share (%)</p>
            </div>
          </div>
          <form onSubmit={handleCommonElecSubmit} className="space-y-3" data-testid="common-elec-form">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Property</Label>
                <select
                  value={commonElecForm.property_id}
                  onChange={(e) => setCommonElecForm({ ...commonElecForm, property_id: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                  required
                  data-testid="common-elec-property-select"
                >
                  <option value="">Select property...</option>
                  {properties.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Total Bill (CEB) <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#64748b] font-medium">LKR</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={commonElecForm.total_bill}
                    onChange={(e) => setCommonElecForm({ ...commonElecForm, total_bill: e.target.value })}
                    placeholder="e.g., 45000.00"
                    className="pl-14"
                    required
                    data-testid="common-elec-total-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Month</Label>
                  <select
                    value={commonElecForm.period_month}
                    onChange={(e) => setCommonElecForm({ ...commonElecForm, period_month: parseInt(e.target.value, 10) })}
                    className="w-full h-10 px-2 rounded-md border border-[#e2e8f0] text-sm bg-white"
                    data-testid="common-elec-month-select"
                  >
                    {MONTH_NAMES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Year</Label>
                  <Input type="number" value={commonElecForm.period_year} min={2020} max={2100}
                    onChange={(e) => setCommonElecForm({ ...commonElecForm, period_year: parseInt(e.target.value, 10) })}
                    data-testid="common-elec-year-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Notes (Optional)</Label>
                <Input
                  value={commonElecForm.notes}
                  onChange={(e) => setCommonElecForm({ ...commonElecForm, notes: e.target.value })}
                  placeholder="e.g., CEB bill #123"
                  data-testid="common-elec-notes-input"
                />
              </div>
            </div>

            {commonElecPreview && (
              <div className="border border-amber-100 rounded-md bg-amber-50/50 p-4 space-y-3" data-testid="common-elec-preview">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <div>
                    <span className="text-[10px] uppercase tracking-widest text-amber-700">Total CEB bill</span>
                    <div className="text-lg font-semibold text-amber-800" data-testid="common-elec-total-preview">
                      LKR {Number(commonElecPreview.total_bill).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="text-xs text-[#334155] text-right">
                    Total assigned share: <b>{Number(commonElecPreview.total_share_percent).toFixed(4)}%</b> · Allocated: <b>LKR {Number(commonElecPreview.allocated).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b>
                    {commonElecPreview.unallocated > 0.01 && (
                      <div className="text-red-600 mt-0.5">Unallocated: LKR {Number(commonElecPreview.unallocated).toLocaleString(undefined, { minimumFractionDigits: 2 })} (shares don't sum to 100%)</div>
                    )}
                  </div>
                </div>
                <div className="max-h-56 overflow-y-auto border border-amber-100 rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bg-white sticky top-0">
                      <tr>
                        <th className="text-left p-2 text-[10px] font-semibold uppercase text-amber-700">Resident</th>
                        <th className="text-right p-2 text-[10px] font-semibold uppercase text-amber-700">Share %</th>
                        <th className="text-right p-2 text-[10px] font-semibold uppercase text-amber-700">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commonElecPreview.splits.map(s => (
                        <tr key={s.resident_id} className="border-t border-amber-100" data-testid={`common-elec-split-${s.resident_id}`}>
                          <td className="p-2">
                            <div className="font-medium">{s.resident_name}</div>
                            <div className="text-[10px] text-[#64748b]">Unit {s.unit_number || '—'}</div>
                          </td>
                          <td className="p-2 text-right">{Number(s.share_percent).toFixed(4)}%</td>
                          <td className={`p-2 text-right font-medium ${s.amount > 0 ? 'text-amber-700' : 'text-[#94a3b8]'}`}>
                            LKR {Number(s.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={submittingCommonElec || !commonElecForm.property_id || !commonElecForm.total_bill}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="submit-common-elec-button"
            >
              {submittingCommonElec ? 'Creating…' : 'Create Common Electricity Bills'}
            </Button>
            {selectedCommonElecProperty && (
              <p className="text-[10px] text-[#94a3b8] text-center">
                Residents with 0% share are skipped. If shares don't sum to 100%, the unallocated portion is not billed to anyone.
              </p>
            )}
          </form>
        </Card>
      )}

      {/* Additional Common Work — one-off building expenses split by share%. */}
      {isAdmin && showPrepare && (
        <Card className="p-6 bg-white border border-[#e2e8f0]" data-testid="common-add-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-md bg-violet-100 flex items-center justify-center">
              <Wrench className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Additional Common Work</h3>
              <p className="text-xs text-[#64748b]">One-off building expenses (lift repair, painting, cleaning…) split across residents by their ownership share (%). Multiple bills per month allowed.</p>
            </div>
          </div>
          <form onSubmit={handleCommonAddSubmit} className="space-y-3" data-testid="common-add-form">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Property</Label>
                <select
                  value={commonAddForm.property_id}
                  onChange={(e) => setCommonAddForm({ ...commonAddForm, property_id: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                  required
                  data-testid="common-add-property-select"
                >
                  <option value="">Select property...</option>
                  {properties.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Total Bill <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#64748b] font-medium">LKR</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={commonAddForm.total_bill}
                    onChange={(e) => setCommonAddForm({ ...commonAddForm, total_bill: e.target.value })}
                    placeholder="e.g., 50000.00"
                    className="pl-14"
                    required
                    data-testid="common-add-total-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Month</Label>
                  <select
                    value={commonAddForm.period_month}
                    onChange={(e) => setCommonAddForm({ ...commonAddForm, period_month: parseInt(e.target.value, 10) })}
                    className="w-full h-10 px-2 rounded-md border border-[#e2e8f0] text-sm bg-white"
                    data-testid="common-add-month-select"
                  >
                    {MONTH_NAMES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Year</Label>
                  <Input type="number" value={commonAddForm.period_year} min={2020} max={2100}
                    onChange={(e) => setCommonAddForm({ ...commonAddForm, period_year: parseInt(e.target.value, 10) })}
                    data-testid="common-add-year-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Notes (Optional)</Label>
                <Input
                  value={commonAddForm.notes}
                  onChange={(e) => setCommonAddForm({ ...commonAddForm, notes: e.target.value })}
                  placeholder="e.g., Contractor invoice #A-2033"
                  data-testid="common-add-notes-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Description <span className="text-red-500">*</span></Label>
              <Input
                value={commonAddForm.description}
                onChange={(e) => setCommonAddForm({ ...commonAddForm, description: e.target.value })}
                placeholder='e.g., "Lift repair — Otis service", "Building painting Q3"'
                required
                data-testid="common-add-description-input"
              />
              <p className="text-[10px] text-[#94a3b8]">Shown to residents on their invoice card and monthly statement email.</p>
            </div>

            {commonAddPreview && (
              <div className="border border-violet-100 rounded-md bg-violet-50/50 p-4 space-y-3" data-testid="common-add-preview">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <div>
                    <span className="text-[10px] uppercase tracking-widest text-violet-700">Total bill</span>
                    <div className="text-lg font-semibold text-violet-800" data-testid="common-add-total-preview">
                      LKR {Number(commonAddPreview.total_bill).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-xs italic text-violet-700 mt-0.5">{commonAddPreview.description}</div>
                  </div>
                  <div className="text-xs text-[#334155] text-right">
                    Total assigned share: <b>{Number(commonAddPreview.total_share_percent).toFixed(4)}%</b> · Allocated: <b>LKR {Number(commonAddPreview.allocated).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b>
                    {commonAddPreview.unallocated > 0.01 && (
                      <div className="text-red-600 mt-0.5">Unallocated: LKR {Number(commonAddPreview.unallocated).toLocaleString(undefined, { minimumFractionDigits: 2 })} (shares don't sum to 100%)</div>
                    )}
                  </div>
                </div>
                <div className="max-h-56 overflow-y-auto border border-violet-100 rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bg-white sticky top-0">
                      <tr>
                        <th className="text-left p-2 text-[10px] font-semibold uppercase text-violet-700">Resident</th>
                        <th className="text-right p-2 text-[10px] font-semibold uppercase text-violet-700">Share %</th>
                        <th className="text-right p-2 text-[10px] font-semibold uppercase text-violet-700">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commonAddPreview.splits.map((s) => (
                        <tr key={`${s.resident_id}-${s.unit_number || ''}`} className="border-t border-violet-100" data-testid={`common-add-split-${s.resident_id}`}>
                          <td className="p-2">
                            <div className="font-medium">{s.resident_name}</div>
                            <div className="text-[10px] text-[#64748b]">Unit {s.unit_number || '—'}</div>
                          </td>
                          <td className="p-2 text-right">{Number(s.share_percent).toFixed(4)}%</td>
                          <td className={`p-2 text-right font-medium ${s.amount > 0 ? 'text-violet-700' : 'text-[#94a3b8]'}`}>
                            LKR {Number(s.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={submittingCommonAdd || !commonAddForm.property_id || !commonAddForm.total_bill || !commonAddForm.description.trim()}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="submit-common-add-button"
            >
              {submittingCommonAdd ? 'Creating…' : 'Create Additional Work Bills'}
            </Button>
            {selectedCommonAddProperty && (
              <p className="text-[10px] text-[#94a3b8] text-center">
                Residents with 0% share are skipped. Multiple bills per month are allowed — each one's description keeps them distinguishable to residents.
              </p>
            )}
          </form>
        </Card>
      )}

      {/* Bulk-email monthly statements — management only. */}
      {isAdmin && showStatements && (
        <Card className="p-6 bg-white border border-[#e2e8f0]" data-testid="email-statements-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-10 w-10 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Mail className="h-5 w-5 text-blue-700" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Email Monthly Statements</h3>
              <p className="text-sm text-[#64748b]">
                Send every resident their consolidated monthly statement (HTML summary + PDF attachment) for the selected period.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Property</Label>
              <select
                value={emailPeriod.property_id}
                onChange={(e) => setEmailPeriod({ ...emailPeriod, property_id: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] bg-white text-sm"
                data-testid="email-period-property"
              >
                <option value="">All properties</option>
                {properties.map(p => (
                  <option key={p._id} value={p._id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Month</Label>
              <select
                value={emailPeriod.period_month}
                onChange={(e) => setEmailPeriod({ ...emailPeriod, period_month: Number(e.target.value) })}
                className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] bg-white text-sm"
                data-testid="email-period-month"
              >
                {MONTH_NAMES_FULL.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Year</Label>
              <Input
                type="number"
                min="2020"
                max="2100"
                value={emailPeriod.period_year}
                onChange={(e) => setEmailPeriod({ ...emailPeriod, period_year: Number(e.target.value) })}
                className="h-10"
                data-testid="email-period-year"
              />
            </div>
            <Button
              onClick={handleEmailStatements}
              disabled={emailingStatements || loadingPreview}
              className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white h-10"
              data-testid="email-statements-button"
            >
              <Mail className="mr-2 h-4 w-4" />
              {loadingPreview ? 'Loading…' : emailingStatements ? 'Sending…' : 'Preview & Send'}
            </Button>
          </div>
          {emailResult && (
            <div className="mt-4 text-sm space-y-1" data-testid="email-statements-result">
              <p className="text-[#0f172a]">
                <strong>Result:</strong> {emailResult.sent_count} sent · {emailResult.failed_count} failed · {emailResult.skipped_no_email_count} skipped (no email)
              </p>
              {emailResult.failed_count > 0 && (
                <details className="text-[#b91c1c]">
                  <summary className="cursor-pointer">View failures ({emailResult.failed_count})</summary>
                  <ul className="mt-2 pl-4 list-disc text-xs">
                    {emailResult.failed.map((f, i) => (
                      <li key={`fail-${f.resident_id || f.email || f.name || i}`}>{f.email || f.name}: {f.error}</li>
                    ))}
                  </ul>
                </details>
              )}
              {emailResult.skipped_no_email_count > 0 && (
                <details className="text-[#b45309]">
                  <summary className="cursor-pointer">View residents with no email ({emailResult.skipped_no_email_count})</summary>
                  <ul className="mt-2 pl-4 list-disc text-xs">
                    {emailResult.skipped_no_email.map((s) => (
                      <li key={`skip-${s.resident_id}`}>Resident ID {s.resident_id}: {s.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </Card>
      )}


      {/* Preview modal — verify recipient list & totals before hitting Send. */}
      {emailPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          role="dialog"
          aria-label="Email statement preview"
          data-testid="email-preview-modal"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            <div className="p-5 border-b border-[#e2e8f0]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                    Review before sending
                  </h3>
                  <p className="text-sm text-[#64748b] mt-1">
                    {MONTH_NAMES_FULL[emailPreview.period_month - 1]} {emailPreview.period_year}
                    {emailPreview.property_name ? ` · ${emailPreview.property_name}` : ' · All properties'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEmailPreview(null)}
                  className="text-[#94a3b8] hover:text-[#0f172a] p-1"
                  aria-label="Close preview"
                  data-testid="close-email-preview"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div className="bg-[#f8fafc] rounded-md p-3">
                  <p className="text-xs text-[#64748b] uppercase tracking-widest">Recipients</p>
                  <p className="text-lg font-semibold text-[#0f172a]" data-testid="preview-total-count">
                    {emailPreview.total_recipients}
                  </p>
                </div>
                <div className="bg-emerald-50 rounded-md p-3">
                  <p className="text-xs text-emerald-700 uppercase tracking-widest">Will email</p>
                  <p className="text-lg font-semibold text-emerald-800">{emailPreview.with_email}</p>
                </div>
                <div className="bg-amber-50 rounded-md p-3">
                  <p className="text-xs text-amber-700 uppercase tracking-widest">No email</p>
                  <p className="text-lg font-semibold text-amber-800">{emailPreview.without_email}</p>
                </div>
                <div className="bg-[#f1f5f9] rounded-md p-3">
                  <p className="text-xs text-[#64748b] uppercase tracking-widest">Total due</p>
                  <p className="text-lg font-semibold text-[#0f172a]">
                    LKR {Number(emailPreview.total_grand_total_due).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <table className="w-full text-sm" data-testid="email-preview-table">
                <thead className="text-xs text-[#64748b] uppercase tracking-widest border-b border-[#e2e8f0]">
                  <tr>
                    <th className="text-left py-2">Resident</th>
                    <th className="text-left py-2">Unit</th>
                    <th className="text-left py-2 hidden md:table-cell">Property</th>
                    <th className="text-left py-2">Email</th>
                    <th className="text-right py-2">Maintenance</th>
                    <th className="text-right py-2">Water</th>
                    <th className="text-right py-2">Common W.</th>
                    <th className="text-right py-2">Common E.</th>
                    <th className="text-right py-2">Add. Work</th>
                    <th className="text-right py-2">Grand total</th>
                    <th className="text-right py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {emailPreview.recipients.map((r) => {
                    const t = r.amount_due_by_type || {};
                    const fmt = (v) => `LKR ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    const zero = (v) => !v || Math.abs(Number(v)) < 0.005;
                    return (
                      <tr
                        key={r.resident_id}
                        className={`border-b border-[#f1f5f9] ${!r.has_email ? 'bg-amber-50/60' : ''}`}
                        data-testid={`preview-row-${r.resident_id}`}
                      >
                        <td className="py-2 pr-3 font-medium text-[#0f172a]">{r.name}</td>
                        <td className="py-2 pr-3 text-[#334155]">{r.unit_number || '—'}</td>
                        <td className="py-2 pr-3 text-[#334155] hidden md:table-cell">{r.property_name || '—'}</td>
                        <td className="py-2 pr-3">
                          {r.has_email ? (
                            <span className="text-[#334155] text-xs">{r.email}</span>
                          ) : (
                            <span className="text-amber-700 text-xs font-medium">No email — will be skipped</span>
                          )}
                        </td>
                        <td className={`py-2 pr-2 text-right ${zero(t.maintenance) ? 'text-[#cbd5e1]' : 'text-[#334155]'}`}>{fmt(t.maintenance)}</td>
                        <td className={`py-2 pr-2 text-right ${zero(t.water) ? 'text-[#cbd5e1]' : 'text-[#334155]'}`}>{fmt(t.water)}</td>
                        <td className={`py-2 pr-2 text-right ${zero(t.common_water) ? 'text-[#cbd5e1]' : 'text-[#334155]'}`}>{fmt(t.common_water)}</td>
                        <td className={`py-2 pr-2 text-right ${zero(t.common_electricity) ? 'text-[#cbd5e1]' : 'text-[#334155]'}`}>{fmt(t.common_electricity)}</td>
                        <td className={`py-2 pr-2 text-right ${zero(t.common_additional) ? 'text-[#cbd5e1]' : 'text-[#334155]'}`}>{fmt(t.common_additional)}</td>
                        <td className="py-2 pr-3 text-right font-semibold text-[#0f172a]">{fmt(r.grand_total_due)}</td>
                        <td className="py-2 text-right">
                          {r.has_email && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={sendingOne === r.resident_id || emailingStatements}
                              onClick={() =>
                                setConfirmOne({
                                  residentId: r.resident_id,
                                  name: r.name,
                                  period_month: emailPreview.period_month,
                                  period_year: emailPreview.period_year,
                                  property_id: emailPreview.property_id,
                                  unit_number: r.unit_number,
                                })
                              }
                              className="h-7 text-xs"
                              data-testid={`send-one-${r.resident_id}`}
                            >
                              <Mail className="h-3 w-3 mr-1" />
                              {sendingOne === r.resident_id ? 'Sending…' : 'Send'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {emailPreview.totals_by_type && (
                  <tfoot className="border-t-2 border-[#e2e8f0]" data-testid="preview-totals-row">
                    <tr>
                      <td className="py-2 pr-3 font-semibold text-[#0f172a]" colSpan={4}>
                        Column totals ({emailPreview.total_recipients} recipient{emailPreview.total_recipients === 1 ? '' : 's'})
                      </td>
                      <td className="py-2 pr-2 text-right font-semibold text-[#0f172a]">
                        LKR {Number(emailPreview.totals_by_type.maintenance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 pr-2 text-right font-semibold text-[#0f172a]">
                        LKR {Number(emailPreview.totals_by_type.water || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 pr-2 text-right font-semibold text-[#0f172a]">
                        LKR {Number(emailPreview.totals_by_type.common_water || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 pr-2 text-right font-semibold text-[#0f172a]">
                        LKR {Number(emailPreview.totals_by_type.common_electricity || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 pr-2 text-right font-semibold text-[#0f172a]">
                        LKR {Number(emailPreview.totals_by_type.common_additional || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 pr-3 text-right font-bold text-[#0f172a]">
                        LKR {Number(emailPreview.total_grand_total_due).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <div className="p-5 border-t border-[#e2e8f0] flex flex-col sm:flex-row gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setEmailPreview(null)}
                disabled={emailingStatements}
                data-testid="cancel-email-preview"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSendStatements}
                disabled={emailingStatements || emailPreview.with_email === 0}
                className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                data-testid="confirm-send-statements"
              >
                <Mail className="mr-2 h-4 w-4" />
                {emailingStatements
                  ? 'Sending…'
                  : `Confirm & send to ${emailPreview.with_email} recipient${emailPreview.with_email === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm dialog for sending a single resident's statement. */}
      {confirmOne && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          role="dialog"
          aria-label="Confirm send statement"
          data-testid="confirm-send-one-modal"
          onClick={() => sendingOne == null && setConfirmOne(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
              Send statement?
            </h3>
            <p className="text-sm text-[#475569] mt-2">
              Email the <b>{MONTH_NAMES_FULL[confirmOne.period_month - 1]} {confirmOne.period_year}</b> monthly statement to <b>{confirmOne.name}</b>?
            </p>
            <p className="text-xs text-[#94a3b8] mt-2">
              This sends an HTML summary + PDF attachment to their email on file. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <Button
                variant="outline"
                onClick={() => setConfirmOne(null)}
                disabled={sendingOne != null}
                data-testid="cancel-send-one"
              >
                Cancel
              </Button>
              <Button
                onClick={() => runSendOneStatement(confirmOne)}
                disabled={sendingOne != null}
                className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                data-testid="confirm-send-one"
              >
                <Mail className="mr-2 h-4 w-4" />
                {sendingOne === confirmOne.residentId ? 'Sending…' : 'Send statement'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Invoices list — residents see one consolidated Monthly Statement card
          per period; admins keep the flat per-invoice list for granular ops. */}
      {(showList || !isAdmin) && (
      <div>
        <h2 className="text-2xl font-medium tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>
          {isAdmin ? `All Invoices (${invoices.length})` : `My Monthly Statements (${statements.length})`}
        </h2>

        {/* Admin filters: property + type + period + resident + group-by */}
        {isAdmin && invoices.length > 0 && (() => {
          // Derive Period + Resident dropdown options from the current invoice
          // list itself so filters only ever show buckets that actually have
          // data. Sorted newest-first / alphabetical respectively.
          const periodMap = new Map(); // 'YYYY-MM' -> label
          const residentMap = new Map(); // resident_id -> name
          invoices.forEach((inv) => {
            const y = inv.period_year;
            const m = inv.period_month;
            if (y && m) {
              const key = `${y}-${String(m).padStart(2, '0')}`;
              if (!periodMap.has(key)) {
                periodMap.set(key, `${MONTH_NAMES_FULL[m - 1]} ${y}`);
              }
            }
            if (inv.resident_id) {
              const name = inv.resident_name || 'Unnamed';
              const unit = inv.unit_number ? ` · ${inv.unit_number}` : '';
              residentMap.set(inv.resident_id, `${name}${unit}`);
            }
          });
          const periodOptions = Array.from(periodMap.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
          const residentOptions = Array.from(residentMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
          const clearAll = () => {
            setInvoicePropertyFilter('');
            setInvoiceTypeFilter('');
            setInvoicePeriodFilter('');
            setInvoiceResidentFilter('');
          };
          const activeCount =
            (invoicePropertyFilter ? 1 : 0) +
            (invoiceTypeFilter ? 1 : 0) +
            (invoicePeriodFilter ? 1 : 0) +
            (invoiceResidentFilter ? 1 : 0);
          return (
            <div className="flex flex-wrap gap-3 mb-4 items-end" data-testid="invoice-filters">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b] mb-1 block">Property</Label>
                <select
                  value={invoicePropertyFilter}
                  onChange={(e) => setInvoicePropertyFilter(e.target.value)}
                  className="h-9 px-3 rounded-md border border-[#e2e8f0] bg-white text-sm min-w-[180px]"
                  data-testid="filter-property"
                >
                  <option value="">All properties</option>
                  {properties.map((p) => (
                    <option key={p._id} value={p._id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b] mb-1 block">Type</Label>
                <select
                  value={invoiceTypeFilter}
                  onChange={(e) => setInvoiceTypeFilter(e.target.value)}
                  className="h-9 px-3 rounded-md border border-[#e2e8f0] bg-white text-sm min-w-[170px]"
                  data-testid="filter-type"
                >
                  <option value="">All types</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="water">Water (private)</option>
                  <option value="common_water">Common Water</option>
                  <option value="common_electricity">Common Electricity</option>
                  <option value="common_additional">Additional Common Work</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b] mb-1 block">Period</Label>
                <select
                  value={invoicePeriodFilter}
                  onChange={(e) => setInvoicePeriodFilter(e.target.value)}
                  className="h-9 px-3 rounded-md border border-[#e2e8f0] bg-white text-sm min-w-[170px]"
                  data-testid="filter-period"
                >
                  <option value="">All periods</option>
                  {periodOptions.map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b] mb-1 block">Resident</Label>
                <select
                  value={invoiceResidentFilter}
                  onChange={(e) => setInvoiceResidentFilter(e.target.value)}
                  className="h-9 px-3 rounded-md border border-[#e2e8f0] bg-white text-sm min-w-[220px] max-w-[280px]"
                  data-testid="filter-resident"
                >
                  <option value="">All residents</option>
                  {residentOptions.map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b] mb-1 block">Group by</Label>
                <select
                  value={invoiceGroupBy}
                  onChange={(e) => setInvoiceGroupBy(e.target.value)}
                  className="h-9 px-3 rounded-md border border-[#e2e8f0] bg-white text-sm min-w-[140px]"
                  data-testid="filter-group-by"
                >
                  <option value="property">Property</option>
                  <option value="resident">Resident</option>
                  <option value="type">Type</option>
                  <option value="period">Period</option>
                </select>
              </div>
              {activeCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAll}
                  className="h-9"
                  data-testid="clear-invoice-filters"
                >
                  Clear filters ({activeCount})
                </Button>
              )}
            </div>
          );
        })()}

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb] mx-auto"></div>
          </div>
        ) : isAdmin ? (
          invoices.length === 0 ? (
            <Card className="p-12 text-center bg-white border border-[#e2e8f0]">
              <Receipt className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
              <p className="text-[#64748b]">No invoices yet</p>
            </Card>
          ) : (() => {
            const filtered = invoices.filter((inv) => {
              if (invoicePropertyFilter && inv.property_id !== invoicePropertyFilter) return false;
              if (invoiceTypeFilter && inv.invoice_type !== invoiceTypeFilter) return false;
              if (invoiceResidentFilter && inv.resident_id !== invoiceResidentFilter) return false;
              if (invoicePeriodFilter) {
                const key = `${inv.period_year}-${String(inv.period_month).padStart(2, '0')}`;
                if (key !== invoicePeriodFilter) return false;
              }
              return true;
            });
            // Group filtered invoices per the "Group by" selector so admins can
            // slice by whichever dimension matters right now.
            const TYPE_LABELS = {
              maintenance: 'Maintenance',
              water: 'Water (Private Meter)',
              common_water: 'Common Water',
              common_electricity: 'Common Electricity',
              common_additional: 'Additional Common Work',
            };
            const groupKeyFor = (inv) => {
              if (invoiceGroupBy === 'resident') return inv.resident_id || '__unassigned__';
              if (invoiceGroupBy === 'type') return inv.invoice_type || '__unknown__';
              if (invoiceGroupBy === 'period') {
                return `${inv.period_year}-${String(inv.period_month).padStart(2, '0')}`;
              }
              return inv.property_id || '__unassigned__';
            };
            const groupLabelFor = (inv) => {
              if (invoiceGroupBy === 'resident') {
                const unit = inv.unit_number ? ` · ${inv.unit_number}` : '';
                return `${inv.resident_name || 'Unassigned resident'}${unit}${inv.property_name ? ` · ${inv.property_name}` : ''}`;
              }
              if (invoiceGroupBy === 'type') return TYPE_LABELS[inv.invoice_type] || (inv.invoice_type || 'Other').replace('_', ' ');
              if (invoiceGroupBy === 'period') {
                return `${MONTH_NAMES_FULL[(inv.period_month || 1) - 1]} ${inv.period_year || ''}`.trim();
              }
              return inv.property_name || '(No property)';
            };
            const groups = new Map();
            filtered.forEach((inv) => {
              const key = groupKeyFor(inv);
              if (!groups.has(key)) groups.set(key, { name: groupLabelFor(inv), sortKey: key, invoices: [] });
              groups.get(key).invoices.push(inv);
            });
            // Period groups should read newest-first; others alphabetical by label.
            const entries = Array.from(groups.entries());
            if (invoiceGroupBy === 'period') {
              entries.sort((a, b) => (a[0] < b[0] ? 1 : -1));
            } else {
              entries.sort((a, b) => a[1].name.localeCompare(b[1].name));
            }
            if (filtered.length === 0) {
              return (
                <Card className="p-12 text-center bg-white border border-[#e2e8f0]">
                  <Receipt className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
                  <p className="text-[#64748b]">No invoices match the current filters</p>
                </Card>
              );
            }
            return (
              <div className="space-y-6" data-testid={`invoice-groups-by-${invoiceGroupBy}`}>
                {entries.map(([key, group]) => {
                  const groupTotal = group.invoices.reduce(
                    (sum, i) => sum + Number(i.balance ?? i.total_due ?? i.amount ?? 0),
                    0
                  );
                  return (
                    <div key={key} data-testid={`invoice-group-${key}`}>
                      <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }}>
                          {group.name} <span className="text-[#94a3b8] font-normal">({group.invoices.length})</span>
                        </h3>
                        <span className="text-xs text-[#64748b]">
                          Outstanding: LKR {groupTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="space-y-3">
                        {group.invoices.map((inv) => (
                          <InvoiceRow
                            key={inv._id}
                            invoice={inv}
                            onPayOnline={handlePayOnline}
                            onMarkPaid={handleMarkPaid}
                            onRecordPayment={openRecordPayment}
                            onEmailStatement={(i) =>
                              setConfirmOne({
                                residentId: i.resident_id,
                                name: i.resident_name || 'this resident',
                                period_month: i.period_month,
                                period_year: i.period_year,
                                property_id: i.property_id,
                                unit_number: i.unit_number,
                              })
                            }
                            isAdmin={isAdmin}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()
        ) : (
          statements.length === 0 ? (
            <Card className="p-12 text-center bg-white border border-[#e2e8f0]">
              <FileText className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
              <p className="text-[#64748b]">No monthly statements yet</p>
            </Card>
          ) : (
            <>
              {/* Resident top-level "Log payment" card — cascade mode.
                  Any amount you pay is applied to your oldest unpaid invoice
                  first, cascading to the next until fully absorbed. */}
              {(() => {
                const totalOutstanding = statements.reduce(
                  (sum, s) => sum + Number(s.grand_total_due || s.outstanding || 0),
                  0
                );
                if (totalOutstanding <= 0) return null;
                const creditBalance = Number((statements[0] || {}).credit_balance || 0);
                return (
                  <Card className="p-5 bg-blue-50 border border-blue-200 mb-4" data-testid="resident-log-payment-card">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex-1 min-w-[240px]">
                        <h3 className="text-lg font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }}>
                          Total outstanding: LKR {totalOutstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </h3>
                        {creditBalance > 0 && (
                          <p className="mt-1 inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800" data-testid="credit-balance-chip">
                            Credit carried forward: LKR {creditBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        )}
                        <p className="text-sm text-[#475569] mt-1">
                          Paid at the bank? Log the total here — applied to your oldest unpaid invoice first, and any surplus becomes a credit for future months. Bank slip is required.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2" data-testid="resident-outstanding-actions">
                        <Button
                          onClick={() => openSubmitPayment({
                            _id: null,                 // cascade — no specific invoice
                            amount: totalOutstanding,
                            total_due: totalOutstanding,
                            balance: totalOutstanding,
                            cascade: true,
                          })}
                          className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                          data-testid="resident-cascade-log-payment"
                        >
                          Log a Payment
                        </Button>
                        <Button
                          onClick={handlePayAllOnline}
                          disabled={payingAll}
                          className="bg-[#059669] hover:bg-[#047857] text-white"
                          data-testid="resident-pay-all-online"
                        >
                          <CreditCard className="mr-1 h-4 w-4" />
                          {payingAll ? 'Redirecting…' : 'Pay All Online'}
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })()}
              {(() => {
                // Multi-unit resident detection: derive the unique set of
                // (property_id, unit_number) pairs across statements. If the
                // resident owns >1 unit, render a chip filter row so they can
                // isolate one unit at a time. This also fixes the duplicate
                // React `key` issue where two statements in the same month
                // (one per unit) would collide.
                const unitKeyOf = (s) => `${s.property_id || ''}::${s.unit_number || ''}`;
                const uniqueUnits = [];
                const seenUnits = new Set();
                for (const s of statements) {
                  const k = unitKeyOf(s);
                  if (!seenUnits.has(k)) {
                    seenUnits.add(k);
                    uniqueUnits.push({
                      key: k,
                      unit_number: s.unit_number,
                      property_name: s.property_name,
                    });
                  }
                }
                const isMultiUnit = uniqueUnits.length > 1;
                const filteredStatements = isMultiUnit && statementUnitFilter !== 'all'
                  ? statements.filter(s => unitKeyOf(s) === statementUnitFilter)
                  : statements;
                return (
                  <>
                    {isMultiUnit && (
                      <div
                        className="flex flex-wrap items-center gap-2 mb-4"
                        data-testid="statement-unit-filter"
                      >
                        <span className="text-xs font-semibold uppercase tracking-widest text-[#64748b] mr-1">
                          Filter by unit
                        </span>
                        <button
                          type="button"
                          onClick={() => setStatementUnitFilter('all')}
                          className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                            statementUnitFilter === 'all'
                              ? 'bg-[#0f172a] text-white border-[#0f172a]'
                              : 'bg-white text-[#64748b] border-[#e2e8f0] hover:border-[#94a3b8]'
                          }`}
                          data-testid="statement-unit-filter-all"
                        >
                          All units ({uniqueUnits.length})
                        </button>
                        {uniqueUnits.map(u => (
                          <button
                            key={u.key}
                            type="button"
                            onClick={() => setStatementUnitFilter(u.key)}
                            className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                              statementUnitFilter === u.key
                                ? 'bg-[#0f172a] text-white border-[#0f172a]'
                                : 'bg-white text-[#64748b] border-[#e2e8f0] hover:border-[#94a3b8]'
                            }`}
                            data-testid={`statement-unit-filter-${u.key}`}
                          >
                            {u.unit_number || '—'} · {u.property_name}
                          </button>
                        ))}
                      </div>
                    )}
                    {isMultiUnit && statementUnitFilter === 'all' && (() => {
                      // "Combined PDF" — one PDF per month with every unit as
                      // its own section. Only offered on the "All units" view
                      // and only for months that actually have statements
                      // across >1 unit (otherwise the per-unit PDF button is
                      // already sufficient).
                      const monthsWithMultiple = {};
                      for (const s of statements) {
                        const mkey = `${s.period_year}-${s.period_month}`;
                        monthsWithMultiple[mkey] = (monthsWithMultiple[mkey] || new Set());
                        monthsWithMultiple[mkey].add(unitKeyOf(s));
                      }
                      const eligibleMonths = Object.entries(monthsWithMultiple)
                        .filter(([_, set]) => set.size > 1)
                        .map(([k]) => {
                          const [y, m] = k.split('-');
                          return { period_year: Number(y), period_month: Number(m) };
                        })
                        .sort((a, b) => (b.period_year - a.period_year) || (b.period_month - a.period_month));
                      if (eligibleMonths.length === 0) return null;
                      return (
                        <div
                          className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-blue-50 border border-blue-100 rounded-md"
                          data-testid="combined-pdf-row"
                        >
                          <span className="text-xs text-blue-900 font-medium mr-1">
                            Consolidated monthly PDF (all units in one file):
                          </span>
                          {eligibleMonths.slice(0, 3).map((m) => (
                            <Button
                              key={`combined-${m.period_year}-${m.period_month}`}
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadCombinedPdf(m.period_year, m.period_month)}
                              disabled={downloadingCombinedPdf}
                              className="h-7 text-xs bg-white"
                              data-testid={`combined-pdf-btn-${m.period_year}-${m.period_month}`}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              {MONTH_NAMES[m.period_month - 1]} {m.period_year}
                            </Button>
                          ))}
                        </div>
                      );
                    })()}
                    <div className="space-y-3" data-testid="statements-list">
                      {filteredStatements.map((s) => (
                        <MonthlyStatementCard
                          key={`${s.period_year}-${s.period_month}-${s.property_id || ''}-${s.unit_number || ''}`}
                          statement={s}
                          onPayOnline={handlePayOnline}
                          onDownloadPdf={handleDownloadStatementPdf}
                          onSubmitPayment={openSubmitPayment}
                        />
                      ))}
                    </div>
                  </>
                );
              })()}
            </>
          )
        )}
      </div>
      )}

      {/* Admin: compact banner linking to the dedicated approvals page.
          Full list + approve/reject actions now live at /payment-approvals. */}
      {isAdmin && showList && pendingApprovals.length > 0 && (
        <Card
          className="p-4 bg-amber-50 border border-amber-200 flex flex-wrap items-center justify-between gap-3"
          data-testid="pending-approvals-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Inbox className="h-5 w-5 text-amber-700" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900" style={{ fontFamily: 'Outfit' }}>
                {pendingApprovals.length} payment approval{pendingApprovals.length === 1 ? '' : 's'} awaiting your review
              </p>
              <p className="text-xs text-amber-800/80">
                Bank-transfer submissions from residents. Approve or reject on the dedicated page.
              </p>
            </div>
          </div>
          <Link to="/payment-approvals" data-testid="open-payment-approvals-link">
            <Button className="bg-amber-600 hover:bg-amber-700 text-white">
              Open Payment Approvals <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </Card>
      )}

      {/* Record-payment modal (admin) */}
      {recordPaymentInvoice && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="record-payment-modal">
          <Card className="p-6 bg-white w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>Record Payment</h3>
            <p className="text-sm text-[#64748b] mb-4">
              {recordPaymentInvoice.resident_name} · balance LKR {Number(recordPaymentInvoice.balance ?? recordPaymentInvoice.total_due ?? recordPaymentInvoice.amount).toFixed(2)}
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Amount (LKR)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={recordPaymentForm.amount}
                  onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, amount: e.target.value })}
                  data-testid="record-payment-amount"
                />
              </div>
              <div>
                <Label className="text-xs">Method</Label>
                <select
                  value={recordPaymentForm.method}
                  onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, method: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] bg-white text-sm"
                  data-testid="record-payment-method"
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Note (optional)</Label>
                <Input
                  value={recordPaymentForm.note}
                  onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, note: e.target.value })}
                  placeholder="e.g., cheque #12345"
                  data-testid="record-payment-note"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6 justify-end">
              <Button variant="outline" onClick={() => setRecordPaymentInvoice(null)} data-testid="record-payment-cancel">Cancel</Button>
              <Button
                onClick={submitRecordPayment}
                disabled={savingRecordPayment}
                className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                data-testid="record-payment-save"
              >
                {savingRecordPayment ? 'Saving…' : 'Record'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Submit-payment modal (resident) */}
      {submitPaymentInvoice && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="submit-payment-modal">
          <Card className="p-6 bg-white w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>Log Bank Transfer Payment</h3>
            <p className="text-sm text-[#64748b] mb-4">
              {submitPaymentInvoice.cascade
                ? `Amount you paid will be applied to your OLDEST unpaid invoice first, then cascade to newer ones. Total outstanding LKR ${Number(submitPaymentInvoice.balance).toFixed(2)}.`
                : `Balance LKR ${Number(submitPaymentInvoice.balance ?? submitPaymentInvoice.total_due ?? submitPaymentInvoice.amount).toFixed(2)}.`}
              {' Management will verify before applying.'}
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Amount you paid (LKR)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={submitPaymentForm.amount}
                  onChange={(e) => setSubmitPaymentForm({ ...submitPaymentForm, amount: e.target.value })}
                  data-testid="submit-payment-amount"
                />
              </div>
              <div>
                <Label className="text-xs">Method</Label>
                <div
                  className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] bg-[#f8fafc] text-sm flex items-center text-[#0f172a]"
                  data-testid="submit-payment-method"
                >
                  Bank transfer
                </div>
                <p className="text-[11px] text-[#64748b] mt-1">
                  Bank transfer only. Please attach your slip below.
                </p>
              </div>
              <div>
                <Label className="text-xs">Note (optional)</Label>
                <Input
                  value={submitPaymentForm.note}
                  onChange={(e) => setSubmitPaymentForm({ ...submitPaymentForm, note: e.target.value })}
                  placeholder="e.g., bank ref no."
                  data-testid="submit-payment-note"
                />
              </div>
              <div>
                <Label className="text-xs">Bank slip (image or PDF)</Label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setSubmitPaymentSlip(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-[#64748b] file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  data-testid="submit-payment-slip"
                />
                {submitPaymentSlip && <p className="text-xs mt-1 text-[#64748b]">{submitPaymentSlip.name}</p>}
              </div>
            </div>
            <div className="flex gap-2 mt-6 justify-end">
              <Button variant="outline" onClick={() => setSubmitPaymentInvoice(null)} data-testid="submit-payment-cancel">Cancel</Button>
              <Button
                onClick={submitSubmitPayment}
                disabled={savingSubmitPayment}
                className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                data-testid="submit-payment-save"
              >
                {savingSubmitPayment ? 'Submitting…' : 'Submit for approval'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
